import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";

/**
 * Catalog autocomplete components (single file).
 *   - default export ItemAutocomplete: the "Item Name" + "SKU" pair in the
 *     Add Item modal (writes to itemFormData / setItemFormData).
 *   - named export GroupItemNameAutocomplete: the grouped-item inline rows
 *     (wraps just the Name input, calls onSelect(name, sku)).
 * Both share ONE catalog cache + search defined below.
 */

// ---- shared catalog cache (loads once per session) ------------------------
let _catalogCache = null;
let _catalogPromise = null;

// Catalog source tables. All are global (no store_id) and share the same
// columns: item_no, name, images. To add a manufacturer later, drop one
// line in here — nothing else in this file needs to change.
const CATALOG_SOURCES = [
  "coaster_products",
  "homelegance_products",
  "acme_products",
  "foa_products",
  "kian_products",
  "ashley_products",
  "flexsteel_products",
];

// Brand label shown next to the item number in the dropdown. Keyed by the
// source table above — add a line here when you add a new vendor table.
// Exported so ScanNoteModal shares the same brand list.
export const CATALOG_BRANDS = {
  coaster_products: "Coaster",
  homelegance_products: "Homelegance",
  acme_products: "ACME",
  foa_products: "Furniture of America",
  kian_products: "Kian USA",
  ashley_products: "Ashley",
  flexsteel_products: "Flexsteel",
};

// The shared catalog (catalog_products, filled from the WonderSign feeds):
// every brand in ONE table, one row per piece, each with a photo. Keyed by
// its `brand` column -> the label shown in the dropdown. A brand that also
// has a legacy table above (Ashley, ACME, Flexsteel) is MERGED with it: the
// shared row wins for an item number both have (it carries the photo and the
// fuller name), and legacy-only item numbers stay searchable as before.
// Not listed on purpose: `coaster` (coaster_products has the same items and
// photos already) and `eastbay` (one store's own line).
const SHARED_CATALOG_TABLE = "catalog_products";
export const SHARED_CATALOG_BRANDS = {
  ashley: "Ashley",
  acme: "ACME",
  flexsteel: "Flexsteel",
  newclassic: "New Classic",
};

// Brand badge colors for the dropdown. Keeps brands visually distinct so two
// near-identical SKUs from different vendors (e.g. Coaster 1080-63 vs
// Flexsteel 1080-63P5) are unmistakable at a glance.
export const CATALOG_BRAND_COLORS = {
  Coaster: "bg-amber-100 text-amber-800",
  Homelegance: "bg-emerald-100 text-emerald-800",
  ACME: "bg-violet-100 text-violet-800",
  "Furniture of America": "bg-sky-100 text-sky-800",
  "Kian USA": "bg-rose-100 text-rose-800",
  Ashley: "bg-blue-100 text-blue-800",
  Flexsteel: "bg-orange-100 text-orange-800",
  "New Classic": "bg-teal-100 text-teal-800",
};

// PostgREST silently caps every select at the project's "Max Rows" setting
// (1000 by default) — no error, just a truncated result. We page through
// the table with .range() so catalogs larger than one page load fully.
const PAGE_SIZE = 1000;

function parseFirstImage(images) {
  try {
    const arr = Array.isArray(images) ? images : JSON.parse(images || "[]");
    return arr[0] || null;
  } catch {
    return null;
  }
}

// Fetch EVERY row from a catalog table, paging past the server row cap.
// IMPORTANT: order by item_no (the primary key), not name — paginating on a
// non-unique sort column can skip or duplicate rows across page boundaries.
async function fetchAllRows(table) {
  const rows = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from(table)
      .select("item_no,name,images")
      .order("item_no", { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE_SIZE) break; // short page = last page
  }
  return rows;
}

// Same paging, for one brand of the shared catalog. Ordered by sku (its
// primary key). `thumb` is the 500px-wide photo the feed stores per piece —
// the full-size image_url is ~20x heavier, far too much for a 40px tile.
async function fetchSharedBrandRows(brand) {
  const rows = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from(SHARED_CATALOG_TABLE)
      .select("item_no,name,thumb:data->thumbs->>0")
      .eq("brand", brand)
      .eq("is_active", true)
      .order("sku", { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < PAGE_SIZE) break; // short page = last page
  }
  return rows;
}

function makeCatalogRow(itemNo, name, brand, image) {
  return {
    sku: itemNo || "",
    name: name || "",
    brand,
    image,
    _skuLower: (itemNo || "").toLowerCase(),
    // Shared-catalog names read "Collection - Piece - Color". Search them
    // without the dashes so typing "tamarack dresser" still finds
    // "Tamarack - Dresser - Black". The name shown and saved is unchanged.
    _nameLower: (name || "").toLowerCase().replace(/\s+-\s+/g, " "),
  };
}

// Exported so ScanNoteModal (and anything else) reuses the same catalog
// loader AND the same in-memory cache — the catalog only ever loads once.
export async function loadCatalog() {
  if (_catalogCache) return _catalogCache;
  if (_catalogPromise) return _catalogPromise;
  _catalogPromise = (async () => {
    // Query every catalog table in parallel so total latency is the
    // slowest single source, not the sum of them. One source failing
    // (e.g. table not created yet) must not break the others.
    const [results, sharedResults] = await Promise.all([
      Promise.all(
        CATALOG_SOURCES.map(async (table) => {
          try {
            return { table, data: await fetchAllRows(table) };
          } catch (error) {
            console.error(`ItemAutocomplete: ${table} load failed`, error);
            return { table, data: [] };
          }
        })
      ),
      Promise.all(
        Object.keys(SHARED_CATALOG_BRANDS).map(async (brand) => {
          try {
            return { brand, data: await fetchSharedBrandRows(brand) };
          } catch (error) {
            console.error(`ItemAutocomplete: ${SHARED_CATALOG_TABLE} (${brand}) load failed`, error);
            return { brand, data: [] };
          }
        })
      ),
    ]);

    // Shared-catalog rows, per brand label, keyed by item number so a legacy
    // row for the same piece can be swapped for its richer twin below.
    const itemKey = (itemNo) => (itemNo || "").trim().toUpperCase();
    const sharedByBrand = {};
    for (const { brand, data } of sharedResults) {
      const label = SHARED_CATALOG_BRANDS[brand];
      const byItem = (sharedByBrand[label] ||= new Map());
      for (const row of data) {
        byItem.set(
          itemKey(row.item_no),
          makeCatalogRow(row.item_no, row.name, label, row.thumb || null)
        );
      }
    }

    const rows = [];
    for (const { table, data } of results) {
      const brand = CATALOG_BRANDS[table] || "";
      const shared = sharedByBrand[brand];
      for (const row of data) {
        const twin = shared && shared.get(itemKey(row.item_no));
        if (twin) {
          rows.push(twin);
          shared.delete(itemKey(row.item_no));
        } else {
          rows.push(
            makeCatalogRow(row.item_no, row.name, brand, parseFirstImage(row.images))
          );
        }
      }
    }
    // Whatever is left has no legacy twin: pieces only the shared catalog
    // knows, and whole brands with no legacy table (New Classic).
    for (const shared of Object.values(sharedByBrand)) {
      rows.push(...shared.values());
    }

    _catalogCache = rows;
    return _catalogCache;
  })();
  return _catalogPromise;
}

function useCatalog() {
  const [catalog, setCatalog] = useState(_catalogCache || []);
  useEffect(() => {
    if (_catalogCache) {
      setCatalog(_catalogCache);
      return;
    }
    let alive = true;
    loadCatalog().then((rows) => {
      if (alive) setCatalog(rows);
    });
    return () => {
      alive = false;
    };
  }, []);
  return catalog;
}

// Exported so the Marketing Studio's product picker ranks results exactly
// like the receipt autocomplete does.
export function searchCatalog(catalog, query, limit = 8) {
  const q = (query || "").trim().toLowerCase();
  if (q.length < 2 || !catalog || catalog.length === 0) return [];

  // Match priority (lower = better):
  //   0  SKU starts with query
  //   1  product name starts with query
  //   2  any WORD in the name starts with query  (e.g. "la" -> "Lakeview")
  //   3  SKU contains query
  //   4  name contains query — ONLY for longer queries (>=3), so a 2-char
  //      string can't match random mid-word substrings like "la" in "Rectangular"
  //   5  multi-word query where EVERY word starts a word in the name, in any
  //      order ("elyza ottoman" -> "Elyza - Oversized Accent Ottoman - Linen").
  //      Last resort only: nothing that matched above is reordered by it.
  //
  // NOTE: we always scan the FULL catalog. The old `scored.length > 80`
  // break could stop scanning before reaching a perfect prefix match that
  // happened to sit late in the array, while weak `includes` matches filled
  // the quota first. A full scan of even ~20k cached rows is sub-millisecond.
  const qWords = q.split(/\s+/);
  const scored = [];
  for (const p of catalog) {
    let score = -1;
    if (p._skuLower.startsWith(q)) {
      score = 0;
    } else if (p._nameLower.startsWith(q)) {
      score = 1;
    } else if (wordStartsWith(p._nameLower, q)) {
      score = 2;
    } else if (p._skuLower.includes(q)) {
      score = 3;
    } else if (q.length >= 3 && p._nameLower.includes(q)) {
      score = 4;
    } else if (
      qWords.length > 1 &&
      qWords.every((w) => wordStartsWith(p._nameLower, w))
    ) {
      score = 5;
    }
    if (score >= 0) scored.push([score, p]);
  }
  scored.sort((a, b) => a[0] - b[0]);

  // ---- collision-aware selection --------------------------------------
  // Default behavior is plain score order (unchanged). But when the strong
  // matches include the SAME normalized SKU prefix coming from DIFFERENT
  // brands — e.g. Coaster "1080-63" and Flexsteel "1080-63P5" — a single
  // brand could fill every slot and bury the twin the user actually wants.
  // In that (and only that) case we guarantee each colliding brand surfaces
  // at least one row before any brand takes a second slot. Non-colliding
  // searches are untouched, so typing a product name behaves exactly as before.
  const ranked = scored.map((s) => s[1]);

  // Detect a collision among the top score tier (SKU-prefix matches, score 0/3).
  // Normalize by stripping trailing letters/suffix so "1080-63" and
  // "1080-63P5-35201" cluster on their shared numeric stem.
  const stem = (sku) => (sku || "").toLowerCase().replace(/[^0-9].*$/, "");
  const topTier = scored.filter(([sc]) => sc <= 3).map((s) => s[1]);
  const brandsByStem = {};
  for (const p of topTier) {
    const k = stem(p.sku);
    if (!k) continue;
    (brandsByStem[k] ||= new Set()).add(p.brand);
  }
  const hasCollision = Object.values(brandsByStem).some((set) => set.size > 1);

  if (!hasCollision) {
    return ranked.slice(0, limit);
  }

  // Collision path: first pass takes the best row from each brand (in score
  // order), second pass fills remaining slots with the rest by score.
  const out = [];
  const usedBrands = new Set();
  const taken = new Set();
  for (let i = 0; i < ranked.length && out.length < limit; i++) {
    const p = ranked[i];
    if (!usedBrands.has(p.brand)) {
      out.push(p);
      usedBrands.add(p.brand);
      taken.add(i);
    }
  }
  for (let i = 0; i < ranked.length && out.length < limit; i++) {
    if (!taken.has(i)) out.push(ranked[i]);
  }
  return out;
}

// True if any whitespace-separated word in `text` begins with `q`.
function wordStartsWith(text, q) {
  let i = 0;
  while (i < text.length) {
    // skip to a word boundary
    if (i === 0 || text[i - 1] === " " || text[i - 1] === "-") {
      if (text.startsWith(q, i)) return true;
    }
    i++;
  }
  return false;
}

/**
 * ItemAutocomplete (default export)
 * Drop-in replacement for the "Item Name" + "SKU" inputs in the Add Item modal.
 */
export default function ItemAutocomplete({
  itemFormData,
  setItemFormData,
  onPick,
  nameClassName = "w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors",
  skuClassName = "w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors uppercase font-mono",
}) {
  const catalog = useCatalog();
  const [open, setOpen] = useState(false);
  const [activeField, setActiveField] = useState(null); // 'name' | 'sku'
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  // close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // compute matches from whichever field is active
  const query =
    activeField === "sku"
      ? (itemFormData.sku || "")
      : (itemFormData.name || "");

  const matches = searchCatalog(catalog, query, 10);

  const choose = (p) => {
    setItemFormData((prev) => ({
      ...prev,
      name: p.name,
      sku: (p.sku || "").toUpperCase(),
      // stash image so receipt/AI layers can use it; harmless if unused
      catalogImage: p.image || null,
    }));
    // let the parent resolve the brand -> manufacturerId (also functional)
    if (onPick) onPick(p);
    setOpen(false);
    setHighlight(0);
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(matches[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && matches.length > 0;

  return (
    <div ref={wrapRef} className="space-y-4">
      {/* Item Name */}
      <div className="relative">
        <label className="block text-sm text-slate-500 mb-2">Item Name *</label>
        <input
          type="text"
          value={itemFormData.name}
          onChange={(e) => {
            setItemFormData({ ...itemFormData, name: e.target.value });
            setActiveField("name");
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => {
            setActiveField("name");
            if ((itemFormData.name || "").trim().length >= 2) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder="e.g. Leather Sofa"
          className={nameClassName}
          autoFocus
          autoComplete="off"
        />
        {showDropdown && activeField === "name" && (
          <Dropdown matches={matches} highlight={highlight} onPick={choose} />
        )}
      </div>

      {/* SKU & Color row — Color stays controlled by the parent below this component */}
      <div className="relative">
        <label className="block text-sm text-slate-500 mb-2">
          Item Number (SKU){" "}
          <span className="text-slate-400 font-normal">— optional</span>
        </label>
        <input
          type="text"
          value={itemFormData.sku}
          onChange={(e) => {
            setItemFormData({
              ...itemFormData,
              sku: e.target.value.toUpperCase(),
            });
            setActiveField("sku");
            setOpen(true);
            setHighlight(0);
          }}
          onFocus={() => {
            setActiveField("sku");
            if ((itemFormData.sku || "").trim().length >= 2) setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder="e.g. SOF-001"
          className={skuClassName}
          autoComplete="off"
        />
        {showDropdown && activeField === "sku" && (
          <Dropdown matches={matches} highlight={highlight} onPick={choose} />
        )}
        <p className="text-xs text-slate-400 mt-1.5 leading-snug">
          No item number? You can still save this item — the system will
          assign one automatically so it can be added to inventory and move
          through delivery the normal way.
        </p>
      </div>
    </div>
  );
}

function Dropdown({ matches, highlight, onPick }) {
  return (
    <ul
      className="absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      {matches.map((p, i) => (
        <li
          key={p.sku + i}
          onMouseDown={(e) => {
            e.preventDefault(); // keep focus, prevent blur-close before pick
            onPick(p);
          }}
          className={
            "flex items-center gap-3 px-3 py-2 cursor-pointer " +
            (i === highlight ? "bg-blue-50" : "hover:bg-slate-50")
          }
        >
          {p.image ? (
            <img
              src={p.image}
              alt=""
              className="w-10 h-10 rounded object-cover flex-shrink-0 bg-slate-100"
              loading="lazy"
            />
          ) : (
            <div className="w-10 h-10 rounded bg-slate-100 flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-sm text-slate-900 truncate">{p.name}</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-slate-500 font-mono">{p.sku}</span>
              {p.brand && (
                <span
                  className={
                    "text-[10px] font-semibold px-1.5 py-0.5 rounded " +
                    (CATALOG_BRAND_COLORS[p.brand] || "bg-slate-100 text-slate-600")
                  }
                >
                  {p.brand}
                </span>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * GroupItemNameAutocomplete (named export)
 * Autocomplete for grouped-item inline rows. Wraps ONLY the Name input;
 * picking a suggestion calls onSelect(name, sku) so the parent writes both
 * fields for that row via handleUpdateGroupItemInline.
 */
export function GroupItemNameAutocomplete({
  value,
  onChange,
  onSelect,
  onSelectMany,
  onEnter,
  inputId,
  placeholder = "Item name",
  className = "w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 transition-colors text-sm",
}) {
  const catalog = useCatalog();
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // Keys (sku|brand) of suggestions ticked for multi-add. Stays open while ticking.
  const [checked, setChecked] = useState({});
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setChecked({});
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = searchCatalog(catalog, value, 10);
  const showDropdown = open && matches.length > 0;

  // Stable identity for a suggestion: SKU + brand (SKUs can repeat across brands).
  const keyOf = (p) => `${(p.sku || "").toUpperCase()}|${p.brand || ""}`;

  const closeAndReset = () => {
    setOpen(false);
    setHighlight(0);
    setChecked({});
  };

  // Single pick (click row text / image / Enter) — fast path, unchanged behavior.
  const choose = (p) => {
    onSelect(p.name, (p.sku || "").toUpperCase(), p.brand || "");
    closeAndReset();
  };

  const toggleCheck = (p) => {
    const k = keyOf(p);
    setChecked((prev) => {
      const next = { ...prev };
      if (next[k]) {
        delete next[k];
      } else {
        next[k] = {
          name: p.name,
          sku: (p.sku || "").toUpperCase(),
          brand: p.brand || "",
          quantity: 1,
        };
      }
      return next;
    });
  };

  // Adjust the quantity of an already-checked suggestion (min 1).
  const setQty = (p, qty) => {
    const k = keyOf(p);
    setChecked((prev) => {
      if (!prev[k]) return prev;
      return { ...prev, [k]: { ...prev[k], quantity: Math.max(1, qty) } };
    });
  };

  const checkedList = Object.values(checked);
  const checkedCount = checkedList.length;
  const totalUnits = checkedList.reduce(
    (sum, c) => sum + (parseInt(c.quantity) || 1),
    0,
  );

  const commitChecked = () => {
    if (checkedCount === 0) return;
    if (!onSelectMany) {
      // Parent didn't supply a bulk handler -> fill current row with first pick.
      const p = checkedList[0];
      onSelect(p.name, p.sku, p.brand);
    } else {
      onSelectMany(checkedList);
    }
    closeAndReset();
  };

  const handleKeyDown = (e) => {
    if (showDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, matches.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
        return;
      }
      if (e.key === " " && matches[highlight]) {
        // Space toggles the highlighted row's checkbox (keeps dropdown open).
        e.preventDefault();
        toggleCheck(matches[highlight]);
        return;
      }
      if ((e.key === "+" || e.key === "=") && matches[highlight]) {
        const p = matches[highlight];
        const entry = checked[keyOf(p)];
        if (entry) {
          e.preventDefault();
          setQty(p, entry.quantity + 1);
          return;
        }
      }
      if ((e.key === "-" || e.key === "_") && matches[highlight]) {
        const p = matches[highlight];
        const entry = checked[keyOf(p)];
        if (entry) {
          e.preventDefault();
          setQty(p, entry.quantity - 1);
          return;
        }
      }
      if (e.key === "Enter") {
        e.preventDefault();
        if (checkedCount > 0) {
          commitChecked();
        } else {
          choose(matches[highlight]);
        }
        return;
      }
      if (e.key === "Escape") {
        closeAndReset();
        return;
      }
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onEnter && onEnter();
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => {
          if ((value || "").trim().length >= 2) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {showDropdown && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
        >
          <ul
            className="max-h-64 overflow-y-auto"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {matches.map((p, i) => {
              const entry = checked[keyOf(p)];
              const isChecked = !!entry;
              const qty = entry ? entry.quantity : 1;
              return (
                <li
                  key={keyOf(p) + i}
                  className={
                    "flex items-center gap-3 px-3 py-2 " +
                    (i === highlight ? "bg-blue-50" : "hover:bg-slate-50")
                  }
                >
                  {/* Checkbox: tick to multi-add. Stops the row's single-pick. */}
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onMouseDown={(e) => e.preventDefault()}
                    onChange={() => toggleCheck(p)}
                    className="w-4 h-4 flex-shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    aria-label={`Select ${p.name}`}
                  />
                  {/* Row body: click = single quick-pick (add just this one). */}
                  <div
                    className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(p);
                    }}
                  >
                    {p.image ? (
                      <img
                        src={p.image}
                        alt=""
                        className="w-9 h-9 rounded object-cover flex-shrink-0 bg-slate-100"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded bg-slate-100 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm text-slate-900 truncate">{p.name}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-500 font-mono">{p.sku}</span>
                        {p.brand && (
                          <span
                            className={
                              "text-[10px] font-semibold px-1.5 py-0.5 rounded " +
                              (CATALOG_BRAND_COLORS[p.brand] || "bg-slate-100 text-slate-600")
                            }
                          >
                            {p.brand}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Qty stepper — only once the row is checked. */}
                  {isChecked && (
                    <div
                      className="flex items-center flex-shrink-0 border border-slate-300 rounded-lg overflow-hidden bg-white"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQty(p, qty - 1);
                        }}
                        disabled={qty <= 1}
                        className="px-2 py-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={qty}
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          const n = parseInt(e.target.value.replace(/[^0-9]/g, ""), 10);
                          setQty(p, Number.isNaN(n) ? 1 : n);
                        }}
                        className="w-9 text-center text-sm font-mono text-slate-900 focus:outline-none bg-white"
                        aria-label="Quantity"
                      />
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setQty(p, qty + 1);
                        }}
                        className="px-2 py-1 text-slate-600 hover:bg-slate-100"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {/* Sticky action bar — appears once anything is ticked. */}
          {checkedCount > 0 && (
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 bg-slate-50">
              <span className="text-xs text-slate-600">
                {checkedCount} {checkedCount === 1 ? "item" : "items"}
                {totalUnits !== checkedCount ? ` · ${totalUnits} units` : ""}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setChecked({});
                  }}
                  className="text-xs px-2 py-1 rounded text-slate-500 hover:text-slate-700"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commitChecked();
                  }}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  Add {checkedCount} {checkedCount === 1 ? "item" : "items"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}